import { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import api, { fileUrl } from "../lib/api";
import { toast } from "sonner";
import { Loader2, Trash2, Sparkles, Save, Mic, Heart, Search, Lightbulb, ArrowRight, RotateCcw, Undo2, Check, PenLine, Wand2, Image as ImageIcon, Plus } from "lucide-react";

const PERIODS = ["My Beginnings", "Childhood", "Growing Up", "Love & Family", "Work & Adventure", "Turning Points", "The Wider World", "Lighter Moments", "People & Influence", "What Life Taught Me"];

export default function MemoryDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [mem, setMem] = useState(null);
  const [tab, setTab] = useState(location.state?.tab === "memoir" ? "memoir" : "original");
  const [original, setOriginal] = useState("");
  const [memoir, setMemoir] = useState("");
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [findLoading, setFindLoading] = useState(false);
  const [insight, setInsight] = useState("");
  const [suggested, setSuggested] = useState("");
  const [prevVersion, setPrevVersion] = useState(null); // holds the text before an AI change, for one-tap undo
  const [showHelp, setShowHelp] = useState(false);
  const [editing, setEditing] = useState(false);
  const [identical, setIdentical] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [movingPeriod, setMovingPeriod] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/memories/${id}`);
      setMem(data); setOriginal(data.original_transcript || ""); setMemoir(data.memoir_draft || "");
    } catch {
      toast.error("That memory couldn't be found.");
      navigate("/memories");
    }
  };
  const loadPhotos = async () => {
    try { const { data } = await api.get(`/memories/${id}/photos`); setPhotos(data); } catch { /* non-blocking */ }
  };
  useEffect(() => { load(); loadPhotos(); }, [id]);

  const movePeriod = async (period) => {
    if (!period || period === mem.period) return;
    setMovingPeriod(true);
    try { await api.put(`/memories/${id}`, { period }); toast.success(`Moved to your ${period.toLowerCase()} memories`); await load(); }
    catch { toast.error("Couldn't move it just now. Please try again."); }
    finally { setMovingPeriod(false); }
  };
  const onPhotoPick = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    setUploadingPhoto(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      await api.post(`/memories/${id}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Photo added to this memory");
      await loadPhotos();
    } catch (err) { toast.error(err.response?.data?.detail || "Couldn't add that photo. Please try again."); }
    finally { setUploadingPhoto(false); }
  };
  const delPhoto = async (pid) => {
    await api.delete(`/photos/${pid}`); setPhotos((ps) => ps.filter((p) => p.id !== pid)); toast.success("Photo removed");
  };
  const saveCaption = async (pid, caption) => {
    try { await api.put(`/photos/${pid}`, { caption }); }
    catch { toast.error("Couldn't save the caption. Please try again."); }
  };

  const saveOriginal = async () => {
    setSaving(true);
    try { await api.put(`/memories/${id}`, { original_transcript: original }); toast.success("Your original words are saved"); }
    catch { toast.error("Couldn't save just now. Please try again."); }
    finally { setSaving(false); }
  };
  const saveMemoir = async () => {
    setSaving(true);
    try { await api.put(`/memories/${id}`, { memoir_draft: memoir }); toast.success("Your polished story is saved"); }
    catch { toast.error("Couldn't save just now. Please try again."); }
    finally { setSaving(false); }
  };
  const generateMemoir = async () => {
    const before = memoir; // remember current so we can offer a way back
    setGenerating(true);
    try {
      const { data } = await api.post(`/memories/${id}/generate-memoir`, { style: "polish" });
      const result = data.memoir_draft || "";
      setMemoir(result); setTab("memoir"); setEditing(false);
      const norm = (t) => (t || "").replace(/\s+/g, " ").trim().toLowerCase();
      setIdentical(norm(result) === norm(original));
      if (before.trim()) setPrevVersion(before);
      toast.success(before.trim() ? "Here's a fresh version — your previous one is safe" : "Polished — written only from your own words");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "We couldn't polish that just now. Please try again.");
    } finally { setGenerating(false); }
  };
  const saveEdits = async () => {
    setSaving(true);
    try { await api.put(`/memories/${id}`, { memoir_draft: memoir, story_reviewed: true }); setEditing(false); toast.success("Your edits are saved"); await load(); }
    catch { toast.error("Couldn't save just now. Please try again."); }
    finally { setSaving(false); }
  };
  const cancelEditing = () => { setMemoir(mem.memoir_draft || ""); setEditing(false); };
  const keepVersion = () => { setPrevVersion(null); markReviewed(); toast.success("Kept this version"); };
  const markReviewed = async () => {
    try { await api.put(`/memories/${id}`, { story_reviewed: true }); }
    catch { /* non-blocking */ }
  };
  const likeVersion = async () => { await saveMemoir(); await markReviewed(); toast.success("Lovely — this is now your story"); };
  const useOriginalWords = async () => {
    setSaving(true);
    try {
      await api.put(`/memories/${id}`, { memoir_draft: original, story_reviewed: true });
      setMemoir(original); setPrevVersion(null);
      toast.success("Kept in your own original words"); await load();
    } catch { toast.error("Couldn't save just now. Please try again."); }
    finally { setSaving(false); }
  };
  const revertVersion = async () => {
    if (prevVersion == null) return;
    setSaving(true);
    try {
      await api.put(`/memories/${id}`, { memoir_draft: prevVersion });
      setMemoir(prevVersion); setPrevVersion(null);
      toast.success("Returned to your previous version"); await load();
    } catch { toast.error("Couldn't restore just now. Please try again."); }
    finally { setSaving(false); }
  };
  const del = async () => {
    if (!window.confirm("Delete this memory? This cannot be undone.")) return;
    await api.delete(`/memories/${id}`); toast.success("Memory deleted"); navigate("/memories");
  };
  const findStory = async () => {
    setFindLoading(true);
    try {
      const { data } = await api.post(`/memories/${id}/find-story`);
      setInsight(data.insight || ""); setSuggested(data.question || "");
      if (!data.insight) toast.error("Couldn't find a thread just now. Please try again.");
    } catch { toast.error("Couldn't do that just now. Please try again."); }
    finally { setFindLoading(false); }
  };
  const restoreMemoir = async (text) => {
    await api.put(`/memories/${id}`, { memoir_draft: text });
    toast.success("Version restored"); await load(); setTab("memoir");
  };

  if (!mem) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-teal" size={28} /></div>;

  return (
    <div className="max-w-2xl mx-auto space-y-6 animate-fade-in">
      <button onClick={() => navigate("/memories")} className="text-subtle hover:text-charcoal text-sm">← All memories</button>
      <div>
        <p className="text-xs uppercase tracking-wide text-sage font-semibold">{mem.period || "Memory"}</p>
        <h1 className="font-serif text-2xl sm:text-3xl text-charcoal mt-1.5">{mem.question_text}</h1>
        {mem.contributed_by && (
          <span data-testid="memory-contributed-by" className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-coral bg-coral/10 px-2.5 py-1 rounded-full">
            <Heart size={12} /> Shared by {mem.contributed_by}
          </span>
        )}
        <div className="mt-3 flex items-center gap-2 flex-wrap" data-testid="memory-move">
          <label htmlFor="memory-period-select" className="text-xs text-subtle">This memory belongs with:</label>
          <select id="memory-period-select" data-testid="memory-period-select" value={mem.period || ""} onChange={(e) => movePeriod(e.target.value)} disabled={movingPeriod}
            className="text-sm rounded-full border border-line bg-white px-3 py-1.5 text-charcoal focus:border-teal outline-none disabled:opacity-60">
            <option value="" disabled>Choose a life stage</option>
            {PERIODS.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
          {movingPeriod && <Loader2 className="animate-spin text-teal" size={14} />}
        </div>
      </div>

      {mem.audio_id && (
        <audio data-testid="memory-audio" controls src={fileUrl(mem.audio_id)} className="w-full">
          Your browser does not support audio.
        </audio>
      )}

      <div className="flex gap-2 bg-cream2 p-1 rounded-full w-fit">
        <button data-testid="tab-original" onClick={() => setTab("original")} className={`px-5 py-2 rounded-full text-sm font-medium transition-colors ${tab === "original" ? "bg-white text-teal shadow-sm" : "text-subtle"}`}>Original</button>
        <button data-testid="tab-memoir" onClick={() => setTab("memoir")} className={`px-5 py-2 rounded-full text-sm font-medium transition-colors ${tab === "memoir" ? "bg-white text-teal shadow-sm" : "text-subtle"}`}>Polished story</button>
      </div>

      {tab === "original" ? (
        <div className="animate-fade-in">
          <p className="text-sm text-subtle mb-2 flex items-center gap-1"><Mic size={14} /> Your original words — always preserved.</p>
          <textarea data-testid="original-editor" value={original} onChange={(e) => setOriginal(e.target.value)}
            className="w-full min-h-[240px] p-5 text-lg leading-relaxed rounded-2xl border border-line bg-white focus:border-teal focus:ring-2 focus:ring-teal/20 outline-none" />
          {mem.followups?.length > 0 && (
            <div className="mt-4 space-y-3">
              {mem.followups.map((f, i) => (
                <div key={`${f.q}-${i}`} className="bg-sagelight rounded-xl p-4">
                  <p className="text-sm font-semibold text-teal">{f.q}</p>
                  <p className="text-charcoal mt-1">{f.a}</p>
                </div>
              ))}
            </div>
          )}
          <div className="mt-4">
            {!insight ? (
              <button data-testid="find-story-btn" onClick={findStory} disabled={findLoading}
                className="inline-flex items-center gap-2 bg-sagelight hover:bg-sage/30 text-teal px-5 py-2.5 rounded-full font-semibold text-sm transition-colors disabled:opacity-60">
                {findLoading ? <><Loader2 className="animate-spin" size={16} /> Looking…</> : <><Search size={16} /> Help me find the story</>}
              </button>
            ) : (
              <div data-testid="find-story-panel" className="bg-cream2 border border-line rounded-2xl p-5">
                <div className="flex items-center gap-2 text-sage font-semibold text-sm"><Lightbulb size={15} /> Juliet sees a story here</div>
                <p className="text-charcoal mt-2 leading-relaxed">{insight}</p>
                {suggested && (
                  <div className="mt-3">
                    <p className="text-teal font-medium">"{suggested}"</p>
                    <button data-testid="find-story-answer-btn" onClick={() => navigate("/answer", { state: { question: { id: null, text: suggested } } })}
                      className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold text-coral hover:gap-2.5 transition-all">
                      Explore this <ArrowRight size={15} />
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
          <button data-testid="save-original-btn" onClick={saveOriginal} disabled={saving}
            className="mt-4 inline-flex items-center gap-2 bg-teal hover:bg-tealdark text-cream px-6 py-2.5 rounded-full font-semibold disabled:opacity-60">
            {saving ? <><Loader2 className="animate-spin" size={16} /> Saving…</> : <><Save size={16} /> Save</>}
          </button>
        </div>
      ) : (
        <div className="animate-fade-in">
          <div className="mb-3 bg-cream2 border border-line rounded-2xl p-4">
            <p className="text-sm text-charcoal/80 leading-relaxed">
              <b className="text-teal">Polishing a memory</b> gently tidies the wording while keeping your facts and your voice —
              nothing is invented. Your original words are always saved, and you can edit the polished version below.
            </p>
          </div>
          {identical && memoir && (
            <div data-testid="identical-note" className="mt-1 mb-3 bg-sagelight border border-sage/30 rounded-2xl px-4 py-3">
              <p className="text-sm text-charcoal/85 leading-relaxed">Your memory was already clear and well expressed, so we've kept it very close to your original words.</p>
            </div>
          )}
          <textarea data-testid="memoir-editor" value={memoir} onChange={(e) => setMemoir(e.target.value)} readOnly={memoir && !editing}
            placeholder="No polished story yet. Tap ‘Polish this memory’ and we'll write a gentle version from your own words."
            className={`w-full min-h-[240px] p-5 text-lg leading-relaxed rounded-2xl border bg-white outline-none ${editing ? "border-teal ring-2 ring-teal/20" : "border-line"} ${memoir && !editing ? "cursor-default" : ""}`} />

          {prevVersion != null && (
            <div data-testid="version-protect-bar" className="mt-3 flex flex-col sm:flex-row sm:items-center gap-2 bg-sagelight border border-sage/30 rounded-2xl px-4 py-3">
              <span className="flex-1 text-sm text-charcoal">This is a fresh version. Keep it, or return to the one you had before.</span>
              <div className="flex gap-2">
                <button data-testid="keep-version-btn" onClick={keepVersion} className="inline-flex items-center gap-1.5 bg-teal text-cream px-4 py-2 rounded-full text-sm font-semibold"><Check size={15} /> Keep this version</button>
                <button data-testid="revert-version-btn" onClick={revertVersion} disabled={saving} className="inline-flex items-center gap-1.5 bg-white border border-line text-charcoal px-4 py-2 rounded-full text-sm font-medium disabled:opacity-60"><Undo2 size={15} /> Return to previous</button>
              </div>
            </div>
          )}

          <div className="mt-4">
            {!memoir ? (
              <button data-testid="generate-memoir-btn" onClick={generateMemoir} disabled={generating}
                className="inline-flex items-center gap-2 bg-coral hover:bg-coraldark text-white px-6 py-2.5 rounded-full font-semibold disabled:opacity-60">
                {generating ? <><Loader2 className="animate-spin" size={16} /> Polishing your memory…</> : <><Sparkles size={16} /> Polish this memory</>}
              </button>
            ) : editing ? (
              <div className="flex flex-wrap gap-3">
                <button data-testid="save-memoir-btn" onClick={saveEdits} disabled={saving}
                  className="inline-flex items-center gap-2 bg-teal hover:bg-tealdark text-cream px-6 py-2.5 rounded-full font-semibold disabled:opacity-60">
                  {saving ? <><Loader2 className="animate-spin" size={16} /> Saving…</> : <><Save size={16} /> Save my edits</>}
                </button>
                <button data-testid="cancel-editing-btn" onClick={cancelEditing} disabled={saving}
                  className="inline-flex items-center gap-2 bg-white border border-line hover:border-sage text-charcoal px-5 py-2.5 rounded-full font-medium disabled:opacity-60">
                  Cancel editing
                </button>
              </div>
            ) : (
              <>
                <div className="flex flex-wrap gap-3">
                  <button data-testid="like-version-btn" onClick={likeVersion} disabled={saving}
                    className="inline-flex items-center gap-2 bg-teal hover:bg-tealdark text-cream px-6 py-2.5 rounded-full font-semibold disabled:opacity-60">
                    {saving ? <Loader2 className="animate-spin" size={16} /> : <Check size={16} />} I like this version
                  </button>
                  <button data-testid="use-original-btn" onClick={useOriginalWords} disabled={saving}
                    className="inline-flex items-center gap-2 bg-white border border-line hover:border-sage text-charcoal px-5 py-2.5 rounded-full font-medium disabled:opacity-60">
                    <Mic size={16} /> Keep my original words
                  </button>
                  <button data-testid="edit-myself-btn" onClick={() => { setEditing(true); setTimeout(() => document.querySelector('[data-testid="memoir-editor"]')?.focus(), 50); }}
                    className="inline-flex items-center gap-2 bg-white border border-line hover:border-sage text-charcoal px-5 py-2.5 rounded-full font-medium">
                    <PenLine size={16} /> Edit it myself
                  </button>
                </div>
                <button data-testid="help-change-btn" onClick={() => setShowHelp((s) => !s)} disabled={generating}
                  className="mt-3 inline-flex items-center gap-2 text-sm text-subtle hover:text-teal font-medium transition-colors disabled:opacity-60">
                  <Wand2 size={15} /> Help me change it
                </button>
                {showHelp && (
                  <div data-testid="help-change-panel" className="mt-3 bg-cream2 border border-line rounded-2xl p-4">
                    <p className="text-sm text-charcoal/80 mb-3">Would you like us to write it again? Your current version stays safe and you can return to it.</p>
                    <button data-testid="generate-memoir-btn" onClick={generateMemoir} disabled={generating}
                      className="inline-flex items-center gap-2 bg-coral hover:bg-coraldark text-white px-5 py-2.5 rounded-full font-semibold disabled:opacity-60">
                      {generating ? <><Loader2 className="animate-spin" size={16} /> Polishing your memory…</> : <><Sparkles size={16} /> Polish it again</>}
                    </button>
                    <p className="mt-3 text-xs text-subtle leading-relaxed">We only use what you told us — we never invent events, feelings or details.</p>
                  </div>
                )}
              </>
            )}
          </div>
          {mem.versions?.length > 0 && (
            <div data-testid="memoir-versions" className="mt-6">
              <h3 className="font-serif text-lg text-charcoal flex items-center gap-2"><RotateCcw size={17} /> Earlier versions</h3>
              <p className="text-subtle text-sm mt-0.5">Your previous drafts are kept safe. Restore any one — your current text becomes a version too, so nothing is lost.</p>
              <div className="mt-3 space-y-2">
                {[...mem.versions].reverse().map((v, i) => (
                  <div key={v.created_at || `${(v.text || "").slice(0, 24)}-${i}`} data-testid={`memoir-version-${i}`} className="bg-cream2 border border-line rounded-xl p-4">
                    <p className="text-sm text-charcoal/80 line-clamp-3 leading-relaxed">{v.text}</p>
                    <button data-testid={`restore-memoir-version-${i}`} onClick={() => restoreMemoir(v.text)}
                      className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold text-teal hover:gap-2.5 transition-all">
                      <RotateCcw size={14} /> Restore this version
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <div data-testid="memory-photos" className="pt-2 border-t border-line">
        <div className="flex items-center justify-between">
          <h3 className="font-serif text-lg text-charcoal flex items-center gap-2"><ImageIcon size={17} /> Photos</h3>
          <label data-testid="add-photo-btn" className="inline-flex items-center gap-1.5 text-sm font-semibold text-teal cursor-pointer hover:gap-2.5 transition-all">
            {uploadingPhoto ? <Loader2 className="animate-spin" size={15} /> : <Plus size={15} />} Add a photo
            <input type="file" accept="image/*" className="hidden" onChange={onPhotoPick} disabled={uploadingPhoto} />
          </label>
        </div>
        <p className="text-subtle text-sm mt-0.5">A photo you add here will appear beside this memory in your book.</p>
        {photos.length > 0 && (
          <div className="mt-3 grid grid-cols-3 sm:grid-cols-4 gap-3">
            {photos.map((p) => (
              <div key={p.id} data-testid={`memory-photo-${p.id}`}>
                <div className="relative group rounded-xl overflow-hidden border border-line bg-white aspect-square">
                  <img src={fileUrl(p.file_id)} alt={p.caption || "Memory photo"} className="w-full h-full object-cover" />
                  <button data-testid={`delete-photo-${p.id}`} onClick={() => delPhoto(p.id)} aria-label="Remove photo"
                    className="absolute top-1.5 right-1.5 w-7 h-7 rounded-full bg-charcoal/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <Trash2 size={14} />
                  </button>
                </div>
                <input data-testid={`photo-caption-${p.id}`} defaultValue={p.caption || ""} placeholder="Add a caption…"
                  onBlur={(e) => saveCaption(p.id, e.target.value)}
                  className="mt-1.5 w-full text-xs px-2.5 py-1.5 rounded-lg border border-line bg-white focus:border-teal focus:ring-1 focus:ring-teal/20 outline-none" />
              </div>
            ))}
          </div>
        )}
      </div>

      <button data-testid="delete-memory-btn" onClick={del} className="inline-flex items-center gap-2 text-destructive/80 hover:text-destructive text-sm pt-4">
        <Trash2 size={15} /> Delete this memory
      </button>
    </div>
  );
}
